import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowLeft, Calendar, Clock, User, ArrowRight, FileText, Search, Eye, Tag, BookOpen, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Couleurs par catégorie
const categoryColors = {
  'Réglementation': 'blue',
  'Technique': 'violet',
  'Guide': 'emerald',
  'Comptabilité': 'amber',
  'Tutoriel': 'cyan',
  'Partenaires': 'rose',
  'Conseil': 'emerald',
  'Facturation électronique 2026': 'blue',
  'IA et automatisation comptable': 'violet',
  'Gestion des impayés': 'amber',
  'Guides par métier': 'emerald'
};

// Images par catégorie
const categoryImages = {
  'Réglementation': 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&auto=format&fit=crop&q=60',
  'Technique': 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop&q=60',
  'Guide': 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop&q=60',
  'Comptabilité': 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=800&auto=format&fit=crop&q=60',
  'Tutoriel': 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop&q=60',
  'Partenaires': 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&auto=format&fit=crop&q=60',
  'Conseil': 'https://images.unsplash.com/photo-1553028826-f4804a6dba3b?w=800&auto=format&fit=crop&q=60',
  'Facturation électronique 2026': 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&auto=format&fit=crop&q=60',
  'IA et automatisation comptable': 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&auto=format&fit=crop&q=60',
  'Gestion des impayés': 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop&q=60',
  'Guides par métier': 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&auto=format&fit=crop&q=60'
};

const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchArticles();
    fetchCategories();
    trackPageView();
  }, [selectedCategory]);

  const trackPageView = async () => {
    try {
      await fetch(`${API_URL}/api/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: 'page_view',
          page: '/blog',
          referrer: document.referrer,
          utm_source: new URLSearchParams(window.location.search).get('utm_source'),
          utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
          utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign')
        })
      });
    } catch (e) {
      // Silent fail
    }
  };

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const url = selectedCategory 
        ? `${API_URL}/api/blog/articles?category=${encodeURIComponent(selectedCategory)}&limit=20`
        : `${API_URL}/api/blog/articles?limit=20`;
      const res = await fetch(url);
      const data = await res.json();
      setArticles(data.articles || []);
    } catch (error) {
      console.error('Erreur chargement articles:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/blog/categories`);
      const data = await res.json();
      setCategories(data || []);
    } catch (error) {
      console.error('Erreur chargement catégories:', error);
    }
  };

  const filteredArticles = articles.filter(article =>
    article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const featuredArticle = filteredArticles[0];
  const otherArticles = filteredArticles.slice(1);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900 dark:text-white">SmartFacture AI</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <Link to="/" className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white flex items-center gap-1">
              <ArrowLeft className="h-4 w-4" />
              Accueil
            </Link>
            <Link to="/register">
              <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white shadow-lg shadow-blue-500/25">
                Essai gratuit
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-600 to-blue-800 dark:from-slate-900 dark:to-slate-950 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-white/20 text-white border-white/30 mb-4">
            <BookOpen className="h-4 w-4 mr-2" />
            Blog & Ressources
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Tout savoir sur l'e-facturation 2026
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-8">
            Guides pratiques, actualités réglementaires et conseils d'experts pour préparer votre entreprise.
          </p>
          
          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher un article..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white dark:bg-slate-800 border-0 rounded-xl text-slate-900 dark:text-white placeholder-slate-500 shadow-xl focus:outline-none focus:ring-2 focus:ring-cyan-400"
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 py-4 sticky top-[73px] z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                !selectedCategory 
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25' 
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              Tous les articles
            </button>
            {categories.map((cat) => (
              <button
                key={cat.name}
                onClick={() => setSelectedCategory(cat.name)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === cat.name 
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25' 
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                {cat.name} ({cat.count})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin h-10 w-10 border-3 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-4 text-slate-500">Chargement des articles...</p>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Aucun article trouvé</h3>
              <p className="text-slate-500">Essayez avec d'autres mots-clés ou catégories.</p>
            </div>
          ) : (
            <>
              {/* Featured Article */}
              {featuredArticle && (
                <Link to={`/blog/${featuredArticle.slug}`} className="group block mb-12">
                  <Card className="overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-300">
                    <div className="grid md:grid-cols-2 gap-0">
                      <div className="relative h-64 md:h-auto overflow-hidden">
                        <img 
                          src={categoryImages[featuredArticle.category] || categoryImages['Guide']}
                          alt={featuredArticle.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent md:hidden" />
                        <Badge className={`absolute top-4 left-4 bg-${categoryColors[featuredArticle.category] || 'blue'}-500 text-white`}>
                          <TrendingUp className="h-3 w-3 mr-1" />
                          Article vedette
                        </Badge>
                      </div>
                      <CardContent className="p-8 flex flex-col justify-center">
                        <Badge className={`w-fit mb-4 bg-${categoryColors[featuredArticle.category] || 'blue'}-100 text-${categoryColors[featuredArticle.category] || 'blue'}-700 dark:bg-${categoryColors[featuredArticle.category] || 'blue'}-900/30 dark:text-${categoryColors[featuredArticle.category] || 'blue'}-300`}>
                          {featuredArticle.category}
                        </Badge>
                        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-4 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {featuredArticle.title}
                        </h2>
                        <p className="text-slate-600 dark:text-slate-400 mb-6 line-clamp-3">
                          {featuredArticle.excerpt}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(featuredArticle.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            5 min
                          </span>
                          {featuredArticle.views > 0 && (
                            <span className="flex items-center gap-1">
                              <Eye className="h-4 w-4" />
                              {featuredArticle.views} vues
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                </Link>
              )}

              {/* Other Articles Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {otherArticles.map((article) => (
                  <Link key={article.slug} to={`/blog/${article.slug}`} className="group">
                    <Card className="h-full overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-xl hover:shadow-blue-500/10 hover:border-blue-500/30 transition-all duration-300">
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={categoryImages[article.category] || categoryImages['Guide']}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      </div>
                      <CardContent className="p-6">
                        <Badge className={`mb-3 bg-${categoryColors[article.category] || 'blue'}-100 text-${categoryColors[article.category] || 'blue'}-700 dark:bg-${categoryColors[article.category] || 'blue'}-900/30 dark:text-${categoryColors[article.category] || 'blue'}-300`}>
                          {article.category}
                        </Badge>
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-slate-600 dark:text-slate-400 text-sm mb-4 line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(article.created_at)}
                          </span>
                          <ArrowRight className="h-5 w-5 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-500">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Prêt pour l'e-facturation 2026 ?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Testez SmartFacture AI gratuitement pendant 7 jours. Aucune carte bancaire requise.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/register"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 hover:bg-blue-50 font-semibold rounded-lg shadow-xl transition-colors"
            >
              Commencer gratuitement
              <ArrowRight className="h-5 w-5 ml-2" />
            </Link>
            <Link 
              to="/simulateur"
              className="inline-flex items-center justify-center px-6 py-3 border border-white text-white hover:bg-white/10 font-semibold rounded-lg transition-colors"
            >
              Tester ma conformité
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400">© 2026 SmartFacture AI. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
